import axios from "axios";
import { jwtDecode } from "jwt-decode";
import { useAuthStore } from "../store/authStore"; // Import the store
import { processApiError, getAuthHeaders } from "../lib/api_utils"; // Add this import
import { cleanupCache } from "@/lib/api_utils";
import { logoutWithPersistencePreservation } from "@/lib/persistence-utils";
import { preloadApiDataByRole } from "@/lib/preload";
import { API_BASE_URL as API_URL } from "@/lib/config/api-base-url";
// Re-exported: several other modules import API_URL from this file directly.
export { API_URL };

// Type definition for requestIdleCallback (not in all TypeScript versions)
declare global {
  interface Window {
    requestIdleCallback: (
      callback: IdleRequestCallback,
      options?: IdleRequestOptions
    ) => number;
    cancelIdleCallback: (handle: number) => void;
  }
}
export type Role =
  | "admin"
  | "property_manager"
  | "company_admin"
  | "home_owner"
  | "resident"
  | "tenant"
  | "account_payables"
  | "account_receivables"
  | "utility_admin"
  | "accountant"
  | "resident_support"
  | "esg";

export const roleMap: Record<Role, string> = {
  admin: "Administrator",
  property_manager: "Property Manager",
  company_admin: "Company Admin",
  utility_admin: "Utility Admin",
  account_payables: "Accounts Payable",
  account_receivables: "Accounts Receivable",
  accountant: "Accountant",
  resident_support: "Resident Support",
  home_owner: "Home Owner",
  tenant: "Tenant",
  resident: "Resident",
  esg: "ESG Manager",
};
// Types
export interface TokenResponse {
  access: string;
  refresh: string;
}

export interface SerializedToken {
  token_type: string;
  exp: number;
  iat: number;
  user_id: number;
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  role: Role;
  company_name: string;
  company_id: string;
  company_type: string;
  onboarding_progress?: any; // Array for B2B signup, Object for resident onboarding, null for none
  company_code?: string;
  has_properties?: boolean;
  has_services?: boolean;
  companyId?: string;
  "company-id"?: string;
  company?: { id: string };
  // Property information for tenant/resident users
  properties?: Array<{
    id: string;
    name: string;
    address: string;
    code: string;
  }>;
  property_ids?: string[];
  is_first_login?: boolean;
  has_custom_password?: boolean;
  funding_source?: string;
  has_bank_account?: boolean;
  is_primary_resident?: boolean;
  collect_arrears?: boolean;
  collect_arrear_mode?: "one_time" | "max_500" | "max_1000";
  collect_arrear_original_amount?: number;
  collect_arrear_amount?: number;
  has_arrears_consent?: boolean;
  // Read-only "Login As" impersonation session (stamped by the backend
  // AdminVerifyCodeView when the login code was minted with readonly=True).
  impersonation_readonly?: boolean;
  impersonated_by?: string;
}

export interface UserCredentials {
  phone?: string;
  email?: string;
  code?: string;
}

/**
 * Request a verification code to be sent to the provided phone number
 */
export const requestPhoneVerification = async (
  phone: string
): Promise<boolean> => {
  try {
    const response = await axios.post(`${API_URL}/api/v1/auth/signin`, {
      phone: phone,
      grant_type: "phone",
    });
    return response.status === 200;
  } catch (error) {
    console.error("Error requesting phone verification:", error);
    processApiError(error, "requestPhoneVerification");
    return false;
  }
};

/**
 * Request authentication using email/username and password (password grant type)
 */
export const requestEmailVerification = async (
  username: string,
  password: string
): Promise<[TokenResponse, SerializedToken]> => {
  try {
    const response = await axios.post<TokenResponse & { is_first_login?: boolean }>(
      `${API_URL}/api/v1/token/`,
      {
        email: username,
        password: password,
      },
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );

    // Parse JWT token
    const token = jwtDecode(response.data.access);
    const tokenWithFirstLogin = { ...token, is_first_login: response.data.is_first_login };

    return [response.data, tokenWithFirstLogin as SerializedToken];
  } catch (error) {
    console.error("Error during password authentication:", error);
    processApiError(error, "requestEmailVerification");
    throw error;
  }
};

/**
 * Verify the code and get the OAuth tokens
 */
export const verifyCode = async (
  credentials: UserCredentials
): Promise<TokenResponse> => {
  try {
    const payload = {
      code: credentials.code,
      grant_type: "verification_code",
      ...(credentials.phone && { phone: credentials.phone }),
      ...(credentials.email && { email: credentials.email }),
    };

    const response = await axios.post<TokenResponse>(
      `${API_URL}/oauth/token/`,
      payload
    );
    return response.data;
  } catch (error) {
    console.error("Error verifying code:", error);
    processApiError(error, "verifyCode");
    throw error;
  }
};

/**
 * Refresh the access token. VAPT #15: the refresh token is an HttpOnly cookie,
 * so we POST with credentials and no body - the backend reads the cookie and
 * rotates BOTH cookies. Nothing is persisted client-side. The optional
 * `refresh_token` arg is retained only for call-site compatibility and ignored.
 */
export const refreshToken = async (
  refresh_token?: string
): Promise<TokenResponse> => {
  try {
    const response = await axios.post<TokenResponse>(
      `${API_URL}/api/v1/token/refresh/`,
      {},
      { withCredentials: true }
    );
    // Body still carries the rotated tokens (dual-mode) for compatibility, but
    // we do NOT store them - the cookies are the source of truth.
    return {
      access: response.data?.access ?? "",
      refresh: response.data?.refresh ?? "",
    };
  } catch (error) {
    // Session-gone (401 after BE fix; 400 until that deploy lands) is expected
    // when the interceptor retries without a refresh cookie. Do not noticeError.
    const status = axios.isAxiosError(error) ? error.response?.status : undefined;
    if (status === 401 || status === 400) {
      throw error;
    }
    console.error("Error refreshing token:", error);
    processApiError(error, "refreshToken");
    throw error;
  }
};

/**
 * VAPT #15: establish the session after a successful login.
 *
 * The access/refresh JWTs are delivered by the backend as HttpOnly cookies, so
 * this NO LONGER writes access_token/refresh_token/company_id/token_payload to
 * localStorage. The `payload` argument is the JWT decoded from the login
 * *response body* (received over TLS, never persisted); we use it to (a) seed
 * the rich in-memory identity in the Zustand store for this session and (b)
 * compute the post-login redirect path (first-login / onboarding / signup steps)
 * that the thin GET /me endpoint cannot drive. On a later hard reload the store
 * re-hydrates from /me instead (see authStore.hydrateFromMe).
 *
 * Signature/return preserved so existing callers compile unchanged.
 */
export const storeAuthTokens = (
  tokens: [TokenResponse, SerializedToken]
): void => {
  const payload = tokens[1];

  // In-memory identity only (no localStorage token/identity persistence).
  useAuthStore.getState().setTokenPayload(payload);

  // Lightweight cross-tab "signed in" marker (replaces token_payload as the
  // cross-tab signal; carries no secret).
  try {
    localStorage.setItem("airbills_signed_in", "1");
  } catch {
    // ignore storage failures (private mode, quota, etc.)
  }


  // Only preload API data if onboarding is complete
  const isOnboardingComplete = payload.has_properties && payload.has_services;
  if (payload.role && isOnboardingComplete) {
    // Start preloading in the background without blocking authentication
    // Use requestIdleCallback for better performance (runs when browser is idle)
    if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
      requestIdleCallback(() => {
        try {
          preloadApiDataByRole(payload.role as Role);
        } catch (error) {
          // Silently catch any errors to not disrupt the main auth flow
          console.warn("Error in API preloading:", error);
        }
      }, { timeout: 5000 }); // Fallback after 5 seconds
    } else {
      // Fallback for browsers without requestIdleCallback
      setTimeout(() => {
        try {
          preloadApiDataByRole(payload.role as Role);
        } catch (error) {
          console.warn("Error in API preloading:", error);
        }
      }, 3000);
    }
  }

  // Check if resident needs onboarding (skip password redirect until onboarding is done)
  const isResidentOnboarding =
    payload.role === "resident" &&
    payload.onboarding_progress &&
    typeof payload.onboarding_progress === "object" &&
    !Array.isArray(payload.onboarding_progress) &&
    payload.onboarding_progress.completed !== true;

  if (isResidentOnboarding) {
    sessionStorage.setItem("auth_redirect_path", "/dashboard");
  } else if (payload.is_first_login) {
    sessionStorage.setItem("auth_redirect_path", "/set-password");
  } else if (payload.onboarding_progress && Array.isArray(payload.onboarding_progress)) {
    // Check if signup is incomplete - redirect to appropriate signup step
    const progress = payload.onboarding_progress;

    if (progress.includes("verify-contacts") && !progress.includes("business-information")) {
      // Continue signup at business information step
      sessionStorage.setItem("auth_redirect_path", "/sign-up?step=2");
    } else if (progress.includes("business-information") && !progress.includes("business-address")) {
      // Continue signup at business address step
      sessionStorage.setItem("auth_redirect_path", "/sign-up?step=3");
    } else if (progress.includes("business-address") && !progress.includes("pricing-plan")) {
      // Continue signup at pricing plan step
      sessionStorage.setItem("auth_redirect_path", "/sign-up?step=4");
    } else if (progress.includes("pricing-plan") && !progress.includes("link-bank")) {
      // Continue signup at link bank step
      sessionStorage.setItem("auth_redirect_path", "/sign-up?step=5");
    } else if (progress.includes("link-bank") && !progress.includes("bill-pay")) {
      // Continue signup at bill pay step
      sessionStorage.setItem("auth_redirect_path", "/sign-up?step=6");
    } else if (progress.length > 0 && progress.length < 7) {
      // If there's any incomplete progress, redirect to signup
      sessionStorage.setItem("auth_redirect_path", "/sign-up");
    } else {
      // Signup appears complete, go to dashboard
      sessionStorage.setItem("auth_redirect_path", "/dashboard");
    }
  } else {
    // Use the saved pre-auth URL if available (e.g., from email notification links)
    const savedRedirect = sessionStorage.getItem("pre_auth_redirect");
    sessionStorage.removeItem("pre_auth_redirect");
    sessionStorage.setItem("auth_redirect_path", savedRedirect || "/dashboard");
  }

};

/**
 * Get the authentication token from either localStorage (sessionToken) or localStorage (access_token).
 * If found in access_token but not sessionToken, copies it to sessionToken for consistency.
 */
export const getAuthenticationToken = (): string | null => {
  if (typeof window === "undefined") return null;

  // Signup flow still uses a short-lived `sessionToken`. VAPT #15: the regular
  // login `access_token` is now an HttpOnly cookie and is no longer readable
  // from JS, so there is no access_token fallback here - authenticated API calls
  // are carried by the cookie (see lib/api_utils credentials handling).
  return localStorage.getItem("sessionToken");
};

/**
 * Get user data from either localStorage (signupData) or localStorage (token_payload).
 * If found in token_payload but not signupData, formats and copies it to signupData.
 */
export const getUserData = (): any | null => {
  if (typeof window === "undefined") return null;

  // First try localStorage (signup flow)
  const storedData = localStorage.getItem("signupData");
  if (storedData) {
    try {
      return JSON.parse(storedData);
    } catch (err) {
      console.error("Error parsing user data from localStorage:", err);
    }
  }

  // VAPT #15: fall back to the /me-hydrated in-memory identity (was
  // localStorage.token_payload).
  const parsedPayload = useAuthStore.getState().token_payload as
    | (SerializedToken & { phone_number?: string })
    | null;
  if (parsedPayload) {
    const userData = {
      id: parsedPayload.user_id || parsedPayload.id,
      firstName: parsedPayload.first_name,
      lastName: parsedPayload.last_name,
      email: parsedPayload.email,
      phoneNumber: parsedPayload.phone_number,
      role: parsedPayload.role,
      companyId: parsedPayload.company_id,
      // Add property information for tenant/resident users
      properties: parsedPayload.properties,
      property_ids: parsedPayload.property_ids,
    };

    // Store in localStorage for consistency with the rest of the signup flow
    localStorage.setItem("signupData", JSON.stringify(userData));
    return userData;
  }

  return null;
};

/**
 * VAPT #15: the access token is an HttpOnly cookie and is no longer readable
 * from JS. Retained for API compatibility; always returns null.
 */
export const getAccessToken = (): string | null => {
  return null;
};

/**
 * Check if the user is authenticated. VAPT #15: based on the presence of the
 * /me-hydrated identity in the store rather than a JS-readable token.
 */
export const isAuthenticated = (): boolean => {
  if (typeof window === "undefined") return false;
  return !!useAuthStore.getState().token_payload;
};

/**
 * Logout by removing tokens from storage while preserving persistence data
 */
export const logout = (): void => {
  // Clear storage items
  if (typeof window !== "undefined") {
    // VAPT 2026-08 #2/#15: best-effort server-side blacklist + cookie clear
    // before we drop local state. The backend reads the refresh token from the
    // HttpOnly cookie (credentials:'include'); a cookie-authenticated unsafe
    // method requires the CSRF double-submit header. Fire-and-forget so logout
    // is never blocked.
    try {
      fetch(`${API_URL}/api/v1/auth/logout/`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          ...getAuthHeaders("POST"),
        },
        keepalive: true,
      }).catch(() => {});
    } catch {
      // ignore — local cleanup below still runs
    }

    // Clean up API cache
    cleanupCache();

    // Enhanced logout that preserves persistence data across user sessions
    logoutWithPersistencePreservation();
  }
};

/**
 * Convert session token to JWT tokens
 */
export const convertSessionTokenToJWT = async (
  sessionToken: string
): Promise<[TokenResponse, SerializedToken]> => {
  try {
    const response = await axios.get<TokenResponse>(
      `${API_URL}/api/v1/auth/jwt-from-session/`,
      {
        headers: {
          Authorization: `Bearer ${sessionToken}`,
          "Content-Type": "application/json",
        },
      }
    );

    // Parse JWT token
    const token = jwtDecode(response.data.access);
    return [response.data, token as SerializedToken];
  } catch (error) {
    console.error("Error converting session token to JWT:", error);
    processApiError(error, "convertSessionTokenToJWT");
    throw error;
  }
};

/**
 * Send OTP for login (supports both email and phone)
 */
export const sendLoginOTP = async (
  type: "email" | "phone",
  identifier: string
): Promise<{
  success: boolean;
  maskedIdentifier?: string;
  message?: string;
}> => {
  try {
    const payload: any = { type };

    if (type === "email") {
      payload.email = identifier;
    } else {
      payload.phoneNumber = identifier;
    }

    const response = await axios.post(
      `${API_URL}/api/v1/auth/login/send-otp/`,
      payload,
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );

    return {
      success: response.data.success,
      maskedIdentifier: response.data.maskedIdentifier,
      message: response.data.message,
    };
  } catch (error: any) {
    console.error("Error sending login OTP:", error);
    
    // Extract error message from various possible structures
    let errorMessage = "Failed to send OTP";
    
    if (axios.isAxiosError(error) && error.response?.data) {
      const errorData = error.response.data;
      // Handle structure: { error: { message: "..." } }
      if (errorData.error?.message) {
        errorMessage = errorData.error.message;
      }
      // Handle structure: { message: "..." }
      else if (errorData.message) {
        errorMessage = errorData.message;
      }
      // Handle structure: { detail: "..." }
      else if (errorData.detail) {
        errorMessage = errorData.detail;
      }
    } else if (error instanceof Error) {
      errorMessage = error.message;
    }

    // Don't call processApiError here - it throws with wrong message format
    throw new Error(errorMessage);
  }
};

/**
 * Verify OTP for login and return JWT tokens
 */
export const verifyLoginOTP = async (
  type: "email" | "phone",
  identifier: string,
  code: string
): Promise<[TokenResponse, SerializedToken]> => {
  try {
    const payload: any = { type, code };

    if (type === "email") {
      payload.email = identifier;
    } else {
      payload.phoneNumber = identifier;
    }

    const response = await axios.post<{
      success: boolean;
      access: string;
      refresh: string;
      is_first_login?: boolean;
      user: any;
    }>(`${API_URL}/api/v1/auth/login/verify-otp/`, payload, {
      headers: {
        "Content-Type": "application/json",
      },
    });

    // Parse JWT token
    const token = jwtDecode(response.data.access);
    const tokenWithFirstLogin = { ...token, is_first_login: response.data.is_first_login };

    // Format response to match existing TokenResponse interface
    const tokenResponse: TokenResponse = {
      access: response.data.access,
      refresh: response.data.refresh,
    };

    return [tokenResponse, tokenWithFirstLogin as SerializedToken];
  } catch (error: any) {
    console.error("Error verifying login OTP:", error);
    
    // Extract error message from various possible structures
    let errorMessage = "Invalid or expired OTP code";
    
    if (axios.isAxiosError(error) && error.response?.data) {
      const errorData = error.response.data;
      // Handle structure: { error: { message: "..." } }
      if (errorData.error?.message) {
        errorMessage = errorData.error.message;
      }
      // Handle structure: { message: "..." }
      else if (errorData.message) {
        errorMessage = errorData.message;
      }
      // Handle structure: { detail: "..." }
      else if (errorData.detail) {
        errorMessage = errorData.detail;
      }
    } else if (error instanceof Error) {
      errorMessage = error.message;
    }

    // Don't call processApiError here - it throws with wrong message format
    throw new Error(errorMessage);
  }
};

/**
 * Refresh the current user's token and state (for when onboarding status changes)
 */
export const refreshUserState = async (): Promise<void> => {
  try {
    // VAPT #15: rotate the auth cookies, then re-hydrate identity from /me.
    // NOTE: /me is thinner than the old JWT payload, so onboarding-only flags
    // that used to ride on the refreshed token are not refreshed here.
    await refreshToken();
    const ok = await useAuthStore.getState().hydrateFromMe();
    if (!ok) throw new Error("Not authenticated");
  } catch (error) {
    console.error("Error refreshing user state:", error);
    throw error;
  }
};

/**
 * Wrapper function to consistently update auth state after onboarding actions
 * This should be called after CSV uploads, service additions, etc.
 */
export const refreshOnboardingState = async (): Promise<void> => {
  try {
    // Add a small delay to ensure backend processing is complete
    await new Promise((resolve) => setTimeout(resolve, 1000));

    // Refresh the user state with updated onboarding flags
    await refreshUserState();

    // Also trigger a re-read from localStorage to pick up any changes
    useAuthStore.getState().refreshState();

  } catch (error) {
    console.error("Failed to refresh onboarding state:", error);
    // Don't throw to avoid breaking the user experience
  }
};

/**
 * Set password (first-time password setup)
 * @param data Password setup data
 * @returns Promise resolving to the API response
 */
export const setPassword = async (data: { password: string; confirmPassword: string }): Promise<any> => {
  try {
    const response = await axios.post(`${API_URL}/api/v1/auth/set-password/`, data, {
      withCredentials: true,
      headers: {
        ...getAuthHeaders('POST'),
        'Content-Type': 'application/json',
      },
    });
    return response.data;
  } catch (error) {
    console.error('Error setting password:', error);
    processApiError(error, 'setPassword');
    throw error;
  }
};

/**
 * Change password (change existing password)
 * @param data Password change data
 * @returns Promise resolving to the API response
 */
export const changePassword = async (data: { 
  currentPassword: string; 
  newPassword: string; 
  confirmPassword: string 
}): Promise<any> => {
  try {
    const response = await axios.post(`${API_URL}/api/v1/auth/change-password/`, data, {
      withCredentials: true,
      headers: {
        ...getAuthHeaders('POST'),
        'Content-Type': 'application/json',
      },
    });
    return response.data;
  } catch (error) {
    console.error('Error changing password:', error);
    processApiError(error, 'changePassword');
    throw error;
  }
};
